import React, { useMemo, useCallback, useRef } from 'react';
import _ from 'underscore';
import {
  TreeItem,
  TreeItemLayout,
  mergeClasses,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ChevronRightRegular, ChevronDownRegular } from '@fluentui/react-icons';

import {
  FlatTreeItemProps,
} from '../types';

const useStyles = makeStyles({
  treeItemLayout: {
    paddingRight: tokens.spacingHorizontalL,
    '& .fui-TreeItemLayout__main': {
      ...shorthands.flex(1),
    },
  },
});

export const FlatTreeItem = React.forwardRef(({
  flatTreeItem,
  onRenderContent,
  isOpen,
  selectionMode,
  isDisabled,
} : FlatTreeItemProps, ref: React.Ref<HTMLDivElement>) => {
  const treeItemsProps = flatTreeItem.getTreeItemProps();
  const contentRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const {
    content,
    ...treeItemProps
  } = treeItemsProps;

  const itemType = useMemo(() => {
    if (treeItemProps.lazyLoad) {
      return 'branch';
    }
    return treeItemProps.itemType;
  }, [treeItemProps.lazyLoad, treeItemProps.itemType, treeItemsProps]);

  const selector = useMemo(() => {
    const hideSelector = (itemType === 'branch' && selectionMode === 'single') || treeItemsProps.hideSelection || selectionMode === 'none';
    if (hideSelector) {
      return null;
    }
    if (isDisabled) {
      return { disabled: true };
    }
    return undefined;
  }, [selectionMode, itemType, treeItemsProps.hideSelection, isDisabled]);

  const expandIcon = useMemo(() => {
    if (itemType === 'leaf') {
      return undefined;
    }
    if (!isOpen) {
      return (<ChevronRightRegular />);
    }
    if (treeItemProps.lazyLoad && treeItemProps.isLoading) {
      return (<Spinner size="tiny" />);
    }
    return (<ChevronDownRegular />);
  }, [treeItemProps.isLoading, isOpen, itemType]);

  const onItemClick = useCallback(() => {
    if (itemType === 'leaf' && (!isDisabled)) {
      (contentRef.current?.parentElement?.parentElement?.firstElementChild?.firstElementChild as HTMLElement)?.click();
    }
  }, [itemType, contentRef, isDisabled]);

  const updatedTreeItemProps = {...treeItemProps, disabled: isDisabled};

  return (
    <TreeItem
      {...treeItemProps}
      itemType={itemType}
      tabIndex={0}
      key={flatTreeItem.value}
    >
      <TreeItemLayout
        className={mergeClasses('tree-item-layout', classes.treeItemLayout)}
        expandIcon={expandIcon}
        selector={selector}
        ref={ref}
        onClick={onItemClick}
      >
        <div ref={contentRef}>{_.isFunction(onRenderContent) ? onRenderContent(updatedTreeItemProps) : content}</div>
      </TreeItemLayout>
    </TreeItem>
  );
});
